import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Link,
  useColorModeValue,
  Icon,
  HStack,
  Badge,
  Button
} from '@chakra-ui/react';
import { 
  CheckCircleIcon, 
  WarningIcon, 
  InfoIcon, 
  TimeIcon 
} from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

const Overview = () => {
  // Demo data showcasing 4 automated scenarios
  const dummyCaseData = [
    // Scenario 1: Approved - All Good (Green)
    {
      caseId: "ZZZ001",
      icd10: "M48.061",
      medNecScore: 85,
      payer: "Medicare",
      carrier: "Carrier A",
      state: "TX",
      totalBilled: "$12,000",
      totalExpected: "$10,000",
      payerResponse: "Approved - $10,000",
      suggestedAction: "No action required",
      scenario: "approved",
      status: "Paid"
    },
    {
      caseId: "ZZZ002",
      icd10: "E78.00, G54.2",
      medNecScore: 92,
      payer: "Aetna",
      carrier: "Aetna",
      state: "CA",
      totalBilled: "$15,000",
      totalExpected: "$14,000",
      payerResponse: "Approved - $14,000",
      suggestedAction: "No action required",
      scenario: "approved",
      status: "Paid"
    },
    {
      caseId: "ZZZ003",
      icd10: "M19.90, M54.2",
      medNecScore: 88,
      payer: "BCBS",
      carrier: "Highmark",
      state: "PA",
      totalBilled: "$18,000",
      totalExpected: "$17,000",
      payerResponse: "Approved - $17,000",
      suggestedAction: "No action required",
      scenario: "approved",
      status: "Paid"
    },

    // Scenario 2: Full Denial - Low Med Necessity (Red)
    {
      caseId: "ZZZ004",
      icd10: "M54.16",
      medNecScore: 35,
      payer: "Cigna",
      carrier: "Cigna",
      state: "FL",
      totalBilled: "$20,000",
      totalExpected: "$18,000",
      payerResponse: "Denied - Medical Necessity",
      suggestedAction: "File Appeal",
      scenario: "denied",
      status: "Denied"
    },
    {
      caseId: "ZZZ005",
      icd10: "M54.2",
      medNecScore: 28,
      payer: "Humana",
      carrier: "Humana",
      state: "IL",
      totalBilled: "$9,500",
      totalExpected: "$9,000",
      payerResponse: "Denied - Insufficient Documentation",
      suggestedAction: "File Appeal",
      scenario: "denied",
      status: "Denied"
    },
    {
      caseId: "ZZZ006",
      icd10: "M48.061",
      medNecScore: 42,
      payer: "Medicare",
      carrier: "Medicare",
      state: "TX",
      totalBilled: "$11,000",
      totalExpected: "$10,000",
      payerResponse: "Denied - Medical Necessity",
      suggestedAction: "File Appeal",
      scenario: "denied",
      status: "Denied"
    },

    // Scenario 3: Partial Denial - Arbitration (Orange)
    {
      caseId: "ZZZ007",
      icd10: "G54.2, M19.90",
      medNecScore: 72,
      payer: "UnitedHealthcare",
      carrier: "UnitedHealthcare",
      state: "OR",
      totalBilled: "$16,000",
      totalExpected: "$15,000",
      payerResponse: "Partial - $9,000",
      suggestedAction: "Submit to Arbitration",
      scenario: "partial",
      status: "Partial"
    },
    {
      caseId: "ZZZ008",
      icd10: "M54.16",
      medNecScore: 68,
      payer: "Aetna",
      carrier: "Aetna",
      state: "NV",
      totalBilled: "$13,500",
      totalExpected: "$12,000",
      payerResponse: "Partial - $7,500",
      suggestedAction: "Submit to Arbitration",
      scenario: "partial",
      status: "Partial"
    },
    {
      caseId: "ZZZ009",
      icd10: "Z79.899, M48.061",
      medNecScore: 75,
      payer: "BCBS",
      carrier: "BCBS",
      state: "NY",
      totalBilled: "$14,000",
      totalExpected: "$13,000",
      payerResponse: "Partial - $8,000",
      suggestedAction: "Submit to Arbitration",
      scenario: "partial",
      status: "Partial"
    },

    // Scenario 4: Pre-submission Review - Low Med Necessity (Yellow)
    {
      caseId: "ZZZ010",
      icd10: "M54.2",
      medNecScore: 38,
      payer: "Cigna",
      carrier: "Cigna",
      state: "FL",
      totalBilled: "$12,000",
      totalExpected: "$11,000",
      payerResponse: "Not Submitted",
      suggestedAction: "Coding Review Required",
      scenario: "review",
      status: "Pending"
    },
    {
      caseId: "ZZZ011",
      icd10: "M19.90",
      medNecScore: 45,
      payer: "Humana",
      carrier: "Humana",
      state: "IL",
      totalBilled: "$8,500",
      totalExpected: "$8,000",
      payerResponse: "Not Submitted",
      suggestedAction: "Coding Review Required",
      scenario: "review",
      status: "Pending"
    },
    {
      caseId: "ZZZ012",
      icd10: "G54.2",
      medNecScore: 33,
      payer: "Medicare",
      carrier: "Carrier B",
      state: "TX",
      totalBilled: "$10,000",
      totalExpected: "$9,500",
      payerResponse: "Not Submitted",
      suggestedAction: "Coding Review Required",
      scenario: "review",
      status: "Pending"
    }
  ];

  const tableBg = useColorModeValue('white', 'gray.800');

  const getRowBg = (scenario) => {
    switch (scenario) {
      case 'approved': return 'green.50';
      case 'denied': return 'red.50';
      case 'partial': return 'orange.50';
      case 'review': return 'yellow.50';
      default: return 'white';
    }
  };

  const getIcon = (scenario) => {
    switch (scenario) {
      case 'approved': return <CheckCircleIcon color="green.500" />;
      case 'denied': return <WarningIcon color="red.500" />;
      case 'partial': return <InfoIcon color="orange.500" />;
      case 'review': return <TimeIcon color="yellow.600" />;
      default: return null;
    }
  };

  const getStatusBadge = (status, scenario) => {
    const colorScheme = {
      'Paid': 'green',
      'Denied': 'red',
      'Partial': 'orange',
      'Pending': 'yellow'
    }[status] || 'gray';

    return <Badge colorScheme={colorScheme} variant="solid">{status}</Badge>;
  };

  const renderActionButton = (row) => {
    if (row.scenario === 'approved') {
      return <Text color="green.600" fontWeight="medium">✓ Complete</Text>;
    }
    
    if (row.scenario === 'denied') {
      return (
        <Button 
          size="sm" 
          colorScheme="red" 
          variant="outline"
          onClick={() => console.log('Navigate to appeal')}
        >
          📄 Auto-Generate Appeal 
                  </Button>
      );
    }
    
    if (row.scenario === 'partial') {
      return (
        <Button 
          size="sm" 
          colorScheme="orange" 
          variant="outline"
          onClick={() => console.log('Navigate to arbitration')}
        >
          ⚖️ Submit to Arbitration
        </Button>
      );
    }
    
    if (row.scenario === 'review') {
      return (
        <Button 
          size="sm" 
          colorScheme="yellow" 
          variant="outline"
          onClick={() => console.log('Navigate to coding review')}
        >
          🔍 Generate AI Coding Review 
        </Button>
      );
    }
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <Heading as="h1" mb={2}>
          Claims Overview Dashboard
        </Heading>
        <Text mb={6} color="gray.600" fontSize="lg">
          Automated claims processing with AI-powered CPT coding, medical necessity scoring, 
          835 ERA analysis, and intelligent action recommendations.
        </Text>
        
        <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg={tableBg} boxShadow="sm">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Status</Th>
                <Th>Case ID</Th>
                <Th>ICD-10 Codes</Th>
                <Th>AI Med Necessity</Th>
                <Th>Payer</Th>
                <Th>State</Th>
                <Th>Billed</Th>
                <Th>Expected</Th>
                <Th>Payer Response</Th>
                <Th>Automated Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {dummyCaseData.map((row) => (
                <Tr key={row.caseId} bg={getRowBg(row.scenario)} _hover={{ bg: getRowBg(row.scenario) }}>
                  <Td>
                    <HStack spacing={2}>
                      {getIcon(row.scenario)}
                      {getStatusBadge(row.status, row.scenario)}
                    </HStack>
                  </Td>
                  <Td fontWeight="bold" color="blue.600">{row.caseId}</Td>
                  <Td fontFamily="mono" fontSize="xs">{row.icd10}</Td>
                  <Td>
                    <Text 
                      fontWeight="bold" 
                      color={row.medNecScore >= 80 ? 'green.600' : row.medNecScore < 50 ? 'red.600' : 'orange.600'}
                    >
                      {row.medNecScore}%
                    </Text>
                  </Td>
                  <Td>{row.payer}</Td>
                  <Td>{row.state}</Td>
                  <Td fontWeight="medium">{row.totalBilled}</Td>
                  <Td>{row.totalExpected}</Td>
                  <Td>
                    <Text 
                      fontSize="sm"
                      color={
                        row.payerResponse.includes('Approved') ? 'green.600' :
                        row.payerResponse.includes('Denied') ? 'red.600' :
                        row.payerResponse.includes('Partial') ? 'orange.600' : 'gray.600'
                      }
                      fontWeight="medium"
                    >
                      {row.payerResponse}
                    </Text>
                  </Td>
                  <Td>
                    {renderActionButton(row)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
            <Tfoot bg="gray.50">
              <Tr>
                <Th>Status</Th>
                <Th>Case ID</Th>
                <Th>ICD-10 Codes</Th>
                <Th>AI Med Necessity</Th>
                <Th>Payer</Th>
                <Th>State</Th>
                <Th>Billed</Th>
                <Th>Expected</Th>
                <Th>Payer Response</Th>
                <Th>Automated Action</Th>
              </Tr>
            </Tfoot>
          </Table>
        </Box>
        
        <HStack justify="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            🤖 Fully automated processing • Real-time 835 ERA ingestion • AI-powered appeals
          </Text>
          <Text fontSize="xs" color="gray.400">
            Last updated: {new Date().toLocaleString()}
          </Text>
        </HStack>
      </Container>
    </Box>
  );
};

export default Overview;